import { watch } from "vue";
import { ResponsiveUtils } from "@allwyn/utils/responsive-utils";
import { useNgCasinoState } from "./use-ng-casino-state";
import { useNgCasinoUtils } from "./use-ng-casino-utils";

const { handleFullscreen } = useNgCasinoUtils();

export function handleFullscreenCapture(casinoModule: typeof import("./ng-casino-module")) {
    if (!casinoModule) return;
    if (!document.fullscreenEnabled || !ResponsiveUtils.isMobile() || ResponsiveUtils.isIos()) return;

    const { currentGameId } = useNgCasinoState();

    let removeListeners: () => void;

    let fullscreenClickOverlay: HTMLDivElement;

    function isFullscreenOpen() {
        return !!document.fullscreenElement;
    }

    function openFullscreen() {
        stopFullscreenOverlay();
        handleFullscreen();
    }

    function startFullscreenOverlay() {
        if (isFullscreenOpen()) return;

        const aborter = new AbortController();

        fullscreenClickOverlay.style.display = "block";

        fullscreenClickOverlay.addEventListener("click", openFullscreen, {
            capture: true,
            signal: aborter.signal,
        });

        removeListeners = () => aborter.abort();
    }

    function stopFullscreenOverlay() {
        if (removeListeners) {
            removeListeners();
            removeListeners = null;
        }
        fullscreenClickOverlay.style.display = "none";
    }

    function toggleFullscreenOverlay() {
        if (isFullscreenOpen()) {
            stopFullscreenOverlay();
        } else {
            startFullscreenOverlay();
        }
    }

    function createFullscreenOverlay() {
        fullscreenClickOverlay = document.createElement("div");
        fullscreenClickOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            z-index: 999;
            display: none;
        `;
        document.body.appendChild(fullscreenClickOverlay);
    }

    function deleteFullscreenOverlay() {
        if (fullscreenClickOverlay) {
            fullscreenClickOverlay.remove();
            fullscreenClickOverlay = null;
        }
    }

    function startFullscreenHandler() {
        if (!fullscreenClickOverlay) {
            createFullscreenOverlay();
        }

        if (!isFullscreenOpen()) {
            startFullscreenOverlay();
        }

        document.addEventListener("fullscreenchange", toggleFullscreenOverlay);
    }

    function stopFullscreenHandler() {
        if (fullscreenClickOverlay) {
            deleteFullscreenOverlay();
        }

        document.removeEventListener("fullscreenchange", toggleFullscreenOverlay);
    }

    watch(
        currentGameId,
        async (val) => {
            if (val !== 0) {
                // game is opened
                startFullscreenHandler();
            } else {
                stopFullscreenHandler();

                if (isFullscreenOpen()) {
                    try {
                        await document.exitFullscreen();
                    } catch (error) {
                        console.error("Failed to exit fullscreen mode:", error);
                    }
                }
            }
        },
        { immediate: true }
    );
}
